/**
 * Query Keys Constants para TanStack Query
 *
 * Estructura recomendada:
 * - Las keys son arrays para permitir jerarquías y filtros
 * - Primera posición: entidad principal (ej: 'condominiums')
 * - Posiciones siguientes: operación o filtros específicos
 *
 * Ejemplo de uso:
 * // En un query service:
 * queryKey: QUERY_KEYS.condominiums.switch()
 *
 * // Para invalidar todas las queries de condominios:
 * queryClient.invalidateQueries({ queryKey: QUERY_KEYS.condominiums.all })
 *
 * // Para invalidar un condominio específico:
 * queryClient.invalidateQueries({
 *   queryKey: QUERY_KEYS.condominiums.detail('abc-123')
 * })
 */

export const QUERY_KEYS = {
  // ============ CONDOMINIUMS ============
  condominiums: {
    all: ['condominiums'] as const,
    lists: () => [...QUERY_KEYS.condominiums.all, 'list'] as const,
    list: (filters?: string) =>
      [...QUERY_KEYS.condominiums.lists(), filters] as const,
    switch: () => [...QUERY_KEYS.condominiums.all, 'switch'] as const,
    details: () => [...QUERY_KEYS.condominiums.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.condominiums.details(), id] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.condominiums.all, 'grid', config] as const,
    dashboard: () => [...QUERY_KEYS.condominiums.all, 'dashboard'] as const,
  },

  // ============ DROPDOWNS ============
  dropdowns: {
    all: ['dropdowns'] as const,
    condominiumTypes: () =>
      [...QUERY_KEYS.dropdowns.all, 'condominium-types'] as const,
    countries: () =>
      [...QUERY_KEYS.dropdowns.all, 'countries'] as const,
    cities: (countryId?: string) =>
      [...QUERY_KEYS.dropdowns.all, 'cities', countryId] as const,
    zones: (condominiumId?: string) =>
      [...QUERY_KEYS.dropdowns.all, 'zones', condominiumId] as const,
    commonAreaStatuses: () =>
      [...QUERY_KEYS.dropdowns.all, 'common-area-statuses'] as const,
    reservationStatuses: () =>
      [...QUERY_KEYS.dropdowns.all, 'reservation-statuses'] as const,
    documents: (condominiumId?: string) =>
      [...QUERY_KEYS.dropdowns.all, 'documents', condominiumId] as const,
  },

  // ============ USER / PROFILE ============
  user: {
    all: ['user'] as const,
    profile: () => [...QUERY_KEYS.user.all, 'profile'] as const,
    settings: () => [...QUERY_KEYS.user.all, 'settings'] as const,
  },

  // ============ USERS (Admin CRUD) ============
  users: {
    all: ['users'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.users.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.users.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.users.details(), id] as const,
  },

  // ============ TENANTS ============
  tenants: {
    all: ['tenants'] as const,
    list: () => [...QUERY_KEYS.tenants.all, 'list'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.tenants.all, 'detail', id] as const,
  },

  // ============ UNITS ============
  units: {
    all: ['units'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.units.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.units.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.units.details(), id] as const,
    residents: (unitId: string) =>
      [...QUERY_KEYS.units.detail(unitId), 'residents'] as const,
    residentHistory: (unitId: string) =>
      [...QUERY_KEYS.units.detail(unitId), 'resident-history'] as const,
  },

  // ============ ZONES ============
  zones: {
    all: ['zones'] as const,
    list: () => [...QUERY_KEYS.zones.all, 'list'] as const,
    grid: (config?: any) => [...QUERY_KEYS.zones.all, 'grid', config] as const,
  },

  // ============ FINANCE ============
  finance: {
    all: ['finance'] as const,
    dashboard: () => [...QUERY_KEYS.finance.all, 'dashboard'] as const,
    unitStatement: (unitId: string) =>
      [...QUERY_KEYS.finance.all, 'unit-statement', unitId] as const,
  },

  invoices: {
    all: ['invoices'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.invoices.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.invoices.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.invoices.details(), id] as const,
    gaps: () => [...QUERY_KEYS.invoices.all, 'gaps'] as const,
  },

  payments: {
    all: ['payments'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.payments.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.payments.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.payments.details(), id] as const,
  },

  expenses: {
    all: ['expenses'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.expenses.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.expenses.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.expenses.details(), id] as const,
  },

  transactions: {
    all: ['transactions'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.transactions.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.transactions.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.transactions.details(), id] as const,
  },

  budgets: {
    all: ['budgets'] as const,
    grid: (config?: any) =>
      [...QUERY_KEYS.budgets.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.budgets.all, 'detail'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.budgets.details(), id] as const,
  },

  accounts: {
    all: ['accounts'] as const,
    list: () => [...QUERY_KEYS.accounts.all, 'list'] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.accounts.all, 'detail', id] as const,
  },

  banks: {
    all: ['banks'] as const,
    list: () => [...QUERY_KEYS.banks.all, 'list'] as const,
  },

  // ============ OPERATIONS ============
  commonAreas: {
    all: ['common-areas'] as const,
    list: () => [...QUERY_KEYS.commonAreas.all, 'list'] as const,
    detail: (id: string) => [...QUERY_KEYS.commonAreas.all, 'detail', id] as const,
    schedules: (commonAreaId: string) => [...QUERY_KEYS.commonAreas.detail(commonAreaId), 'schedules'] as const,
    exceptions: (commonAreaId: string) => [...QUERY_KEYS.commonAreas.detail(commonAreaId), 'exceptions'] as const,
  },
  reservations: {
    all: ['reservations'] as const,
    list: (filter?: any) => [...QUERY_KEYS.reservations.all, 'list', filter] as const,
  },
  maintenanceTickets: {
    all: ['maintenance-tickets'] as const,
    grid: (config?: any) => [...QUERY_KEYS.maintenanceTickets.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.maintenanceTickets.all, 'detail', id] as const,
  },
  inventoryItems: {
    all: ['inventory-items'] as const,
    grid: (config?: any) => [...QUERY_KEYS.inventoryItems.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.inventoryItems.all, 'detail', id] as const,
  },
  movingRequests: {
    all: ['moving-requests'] as const,
    grid: (config?: any) => [...QUERY_KEYS.movingRequests.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.movingRequests.all, 'detail', id] as const,
  },

  // ============ COMMUNITY ============
  employees: {
    all: ['employees'] as const,
    grid: (config?: any) => [...QUERY_KEYS.employees.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.employees.all, 'detail'] as const,
    detail: (id: string) => [...QUERY_KEYS.employees.details(), id] as const,
  },
  council: {
    all: ['council'] as const,
    grid: (config?: any) => [...QUERY_KEYS.council.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.council.all, 'detail'] as const,
    detail: (id: string) => [...QUERY_KEYS.council.details(), id] as const,
  },
  legalCompliance: {
    all: ['legal-compliance'] as const,
    grid: (config?: any) => [...QUERY_KEYS.legalCompliance.all, 'grid', config] as const,
    details: () => [...QUERY_KEYS.legalCompliance.all, 'detail'] as const,
    detail: (id: string) => [...QUERY_KEYS.legalCompliance.details(), id] as const,
  },
  chat: {
    all: ['chat'] as const,
    channels: () => [...QUERY_KEYS.chat.all, 'channels'] as const,
    messages: (channelId: string) => [...QUERY_KEYS.chat.all, 'messages', channelId] as const,
  },

  // ============ STORAGE ============
  storage: {
    all: ['storage'] as const,
    list: (module: string, subFolder?: string, maxKeys?: number) =>
      [...QUERY_KEYS.storage.all, 'list', module, subFolder ?? '', maxKeys ?? ''] as const,
  },

  // ============ SECURITY ============
  visits: {
    all: ['visits'] as const,
    grid: (config?: any) => [...QUERY_KEYS.visits.all, 'grid', config] as const,
  },
  packages: {
    all: ['packages'] as const,
    grid: (config?: any) => [...QUERY_KEYS.packages.all, 'grid', config] as const,
  },
  surveillanceLogs: {
    all: ['surveillance-logs'] as const,
    grid: (config?: any) => [...QUERY_KEYS.surveillanceLogs.all, 'grid', config] as const,
  },

  // ============ COMMUNITY (COMMUNICATION) ============
  circulars: {
    all: ['circulars'] as const,
    grid: (config?: any) => [...QUERY_KEYS.circulars.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.circulars.all, 'detail', id] as const,
  },
  assemblies: {
    all: ['assemblies'] as const,
    grid: (config?: any) => [...QUERY_KEYS.assemblies.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.assemblies.all, 'detail', id] as const,
  },
  votes: {
    all: ['votes'] as const,
    grid: (config?: any) => [...QUERY_KEYS.votes.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.votes.all, 'detail', id] as const,
  },
  suppliers: {
    all: ['suppliers'] as const,
    grid: (config?: any) => [...QUERY_KEYS.suppliers.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.suppliers.all, 'detail', id] as const,
  },
  alliedServices: {
    all: ['allied-services'] as const,
    grid: (config?: any) => [...QUERY_KEYS.alliedServices.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.alliedServices.all, 'detail', id] as const,
  },
  quotations: {
    all: ['quotations'] as const,
    grid: (config?: any) => [...QUERY_KEYS.quotations.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.quotations.all, 'detail', id] as const,
  },
  marketplaceContracts: {
    all: ['marketplace-contracts'] as const,
    grid: (config?: any) => [...QUERY_KEYS.marketplaceContracts.all, 'grid', config] as const,
    detail: (id: string) => [...QUERY_KEYS.marketplaceContracts.all, 'detail', id] as const,
  },
  communicationTemplates: {
    all: ['communication-templates'] as const,
    list: (type?: number) =>
      [...QUERY_KEYS.communicationTemplates.all, 'list', type] as const,
  },
} as const;

